import { useEffect, useState } from "react";

/**
 * Animate an integer from 0 to `target` over `durationMs` using
 * requestAnimationFrame and an ease-out curve. Returns the current value.
 * Honours prefers-reduced-motion: when set, returns the target immediately.
 */
export function useCountUp(target: number | null | undefined, durationMs = 900): number {
  const finalValue = typeof target === "number" && Number.isFinite(target) ? Math.max(0, Math.round(target)) : 0;
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (finalValue === 0) {
      setValue(0);
      return;
    }
    const prefersReducedMotion = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      setValue(finalValue);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = Math.min(1, (now - start) / durationMs);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - elapsed, 3);
      setValue(Math.round(finalValue * eased));
      if (elapsed < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [finalValue, durationMs]);

  return value;
}
